import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import useCenterlineAiStyles from './styles';
import { isBusy, showsWelcome } from './core/chat-reducer';
import { runOptionsVisible, voiceInputEnabled, voiceLocalOnly } from './core/config';
import { appendDictation } from './core/speech';
import { useDictation } from './useDictation';
import { useCenterlineAi } from './CenterlineAiProvider';
import ToolIcon from './ToolIcon';
import ContextPicker, { ContextChips, KEEP_PICKER_ATTR } from './ContextPicker';
import RunOptionsBar from './RunOptionsBar';
import { CloseGlyph, MicGlyph, PlusGlyph, SendGlyph, StopGlyph } from './Glyphs';

/**
 * The composer (chat-ui-spec §2.1–2.2, §2.7, §4): the "+" button that opens the document context
 * picker, the tool chip and context chips, an auto-sizing textarea, and Send, which turns into
 * Stop while a turn runs.
 */

const SHOW_RUN_OPTIONS = runOptionsVisible(process.env.REACT_APP_CENTERLINE_AI_RUN_OPTIONS);
const MAX_HEIGHT = 160;
/** Phones and tablets dictate through their keyboard's own microphone. */
const TOUCH_FIRST = '(hover: none) and (pointer: coarse)';

/** The CSS variable the popover styles read (styles.ts). */
const POPOVER_ROOM = '--cl-ai-popover-room';

export const DISCLAIMER = 'CENTERLINE AI can make mistakes. Check important info.';

export default function Composer() {
  const classes = useCenterlineAiStyles();
  const { state, actions, inputRef, compact } = useCenterlineAi();
  const [value, setValue] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const busy = isBusy(state);
  const welcome = showsWelcome(state) && !compact;
  const pickerOpen = state.context.pickerOpen && !compact;
  const canSend = !busy && !!state.projectId && value.trim().length > 0;
  // Read per render rather than at load, so tests can switch the build flags.
  const VOICE_INPUT = voiceInputEnabled(process.env.REACT_APP_CENTERLINE_AI_VOICE_INPUT);
  const VOICE_LOCAL_ONLY = voiceLocalOnly(process.env.REACT_APP_CENTERLINE_AI_VOICE_LOCAL_ONLY);
  const touchFirst = useMediaQuery(TOUCH_FIRST, { noSsr: true });
  const caretToEnd = useRef(false);
  const dictation = useDictation(
    (text) => {
      caretToEnd.current = true;
      setValue((v) => appendDictation(v, text));
    },
    {
      enabled: VOICE_INPUT && !touchFirst,
      lang: (typeof navigator !== 'undefined' && navigator.language) || 'en-US',
      localOnly: VOICE_LOCAL_ONLY,
      projectId: state.projectId,
    },
  );
  const showMic =
    VOICE_INPUT &&
    !touchFirst &&
    dictation.supported &&
    (!VOICE_LOCAL_ONLY || !!dictation.onDevice);
  const { listening, cancel: cancelDictation } = dictation;

  // Dictated words land at the end of the text, where the caret follows.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (caretToEnd.current && el && document.activeElement === el) {
      el.setSelectionRange(el.value.length, el.value.length);
    }
    caretToEnd.current = false;
  }, [value, inputRef]);

  // A turn starts: stop listening and drop what wasn't delivered yet.
  useEffect(() => {
    if (busy) cancelDictation();
  }, [busy, cancelDictation]);

  // When listening ends the user is back in the box; Escape ends it (the document picker keeps its own).
  const wasListening = useRef(false);
  useEffect(() => {
    if (wasListening.current && !listening) inputRef.current?.focus();
    wasListening.current = listening;
  }, [listening, inputRef]);
  useEffect(() => {
    if (!listening) return undefined;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) dictation.stop();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [listening, dictation]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [value, inputRef]);

  // While an empty chat is centred, the document picker opens upward into the card's top
  // part; tell it how much room that is, so it doesn't run past the card (which clips it).
  useLayoutEffect(() => {
    const form = formRef.current;
    const card = form?.closest('section');
    if (!form || !card || !welcome) {
      form?.style.removeProperty(POPOVER_ROOM);
      return undefined;
    }
    const measure = () => {
      const room = form.getBoundingClientRect().top - card.getBoundingClientRect().top - 16;
      form.style.setProperty(POPOVER_ROOM, `${Math.max(0, Math.floor(room))}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });

  const submit = () => {
    const text = value.trim();
    if (!canSend || !text) return;
    cancelDictation();
    actions.send(text);
    setValue('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const composing = e.nativeEvent.isComposing || e.keyCode === 229;
    if (e.key === 'Enter' && !e.shiftKey && !composing) {
      e.preventDefault();
      submit();
      return;
    }
    if (e.key === 'Escape' && pickerOpen) {
      e.preventDefault();
      actions.closePicker();
    }
  };

  return (
    <div className={clsx(classes.composerBar, welcome && classes.composerBarWelcome)}>
      <form
        ref={formRef}
        className={classes.composer}
        aria-label="Ask CENTERLINE AI"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {SHOW_RUN_OPTIONS ? <RunOptionsBar /> : null}
        {pickerOpen ? <ContextPicker /> : null}
        <div className={classes.composerRow}>
          <div className={clsx(classes.field, busy && classes.fieldBusy)}>
            {compact ? null : (
              <button
                type="button"
                className={clsx(classes.toolButton, pickerOpen && classes.toolButtonOpen)}
                aria-label="Add documents"
                aria-haspopup="dialog"
                aria-expanded={pickerOpen}
                // Part of the picker, so clicking it while open toggles rather than click-away
                // closing the picker and this click reopening it.
                {...{ [KEEP_PICKER_ATTR]: '' }}
                onClick={() => (pickerOpen ? actions.closePicker() : actions.openPicker())}
              >
                <PlusGlyph />
              </button>
            )}
            {state.activeTool ? (
              <span className={clsx(classes.chip, classes.chipTool)} title={state.activeTool}>
                <ToolIcon tool={state.activeTool} size={13} />
                <span className={classes.chipLabel}>{state.activeTool}</span>
                <button
                  type="button"
                  className={classes.chipX}
                  aria-label="Remove selected tool"
                  onClick={() => {
                    actions.setTool(null);
                    inputRef.current?.focus();
                  }}
                >
                  <CloseGlyph />
                </button>
              </span>
            ) : null}
            {compact ? null : <ContextChips />}
            <textarea
              ref={inputRef}
              rows={1}
              className={classes.textarea}
              placeholder={
                compact
                  ? 'Ask a follow-up about this document…'
                  : 'Ask a question about this project'
              }
              aria-label="Ask a question"
              value={value}
              disabled={busy}
              // The page exists to ask a question, so the box starts focused.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={!compact}
              onChange={(e) => {
                if (dictation.error) dictation.clearError();
                setValue(e.target.value);
              }}
              onKeyDown={onKeyDown}
              {...{ [KEEP_PICKER_ATTR]: '' }}
            />
            {showMic ? (
              <button
                type="button"
                className={clsx(classes.micButton, listening && classes.micButtonListening)}
                aria-label={listening ? 'Stop dictation' : 'Dictate'}
                aria-pressed={listening}
                title={
                  dictation.blocked
                    ? dictation.error ?? 'Microphone blocked.'
                    : listening
                    ? 'Stop dictation'
                    : 'Dictate (click to start)'
                }
                disabled={busy || dictation.blocked}
                onClick={() => (listening ? dictation.stop() : dictation.start())}
              >
                <MicGlyph />
              </button>
            ) : null}
          </div>
          {busy ? (
            <button
              type="button"
              className={clsx(classes.sendButton, classes.stopButton)}
              aria-label="Stop"
              title="Stop"
              onClick={actions.stop}
            >
              <StopGlyph />
            </button>
          ) : (
            <button
              type="submit"
              className={classes.sendButton}
              aria-label="Send"
              title="Send"
              disabled={!canSend}
            >
              <SendGlyph />
            </button>
          )}
        </div>
        {showMic ? (
          <p className={classes.dictationHint} aria-live="polite">
            {listening ? (
              <>
                Listening…{' '}
                {dictation.interim ? (
                  <span className={classes.dictationInterim}>{dictation.interim}</span>
                ) : null}
              </>
            ) : (
              dictation.error
            )}
          </p>
        ) : null}
        <p className={classes.disclaimer}>{DISCLAIMER}</p>
      </form>
    </div>
  );
}
